import type { Building } from "building-types/building/buildingTypes";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type {
  AggregatedKeyAttributesAndMeasures,
  EntityKeyAttributes,
  EntityKeyMeasures,
  KeyAttribute,
} from "building-types/dataSequences/measures";
import type {
  BuiltInDataTableSettings,
  ColumnHeaderOptions,
  KeyAttributeSettingOptions,
  KeyMeasureSettingOptions,
} from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { encodeColumnConfigId } from "building-types/dataTables/dataTableTools";
import type { Table } from "common/data/table";
import { stringContainsOnlyNumericCharacters, formatInchesFraction } from "doc/measurementUtils";
import { keyAttributeNew } from "doc/utils/dataSequenceTools";
import type { DataTablePricingContext } from "lib/options/calculatePricing";
import { buildingOptionKAMs } from "./builtin/building";
import { buildingDoorKAMs } from "./builtin/doors";
import type {
  BuiltInDataSequenceSelector,
  BuiltInDataTable,
  BuiltInDataTableCell,
  BuiltInKAMDataSequenceSelector,
} from "./dataSequenceCatalog";
import { BUILTIN_DATASEQUENCE_CATALOG } from "./dataSequenceCatalog";
import { makeBuiltInKAMDataSequence } from "./dataSequences";
import type { FindAndReplaceFunc } from "./dataTableTypes";

export interface DataTableCellWithIndex {
  readonly index: number;
  readonly cell: BuiltInDataTableCell;
}

/** Generates additional headers */
type DataTableHeaderGenerator = (headers: Table<ColumnHeaderOptions>) => Table<ColumnHeaderOptions>;
/** Generates additional schedule cells based on entity attributes */
type DataTableCellGenerator = (attributes: EntityKeyAttributes, maxIndex: number) => DataTableCellWithIndex[];

/** Finds and replaces values in the schedule */
// Re-export from shared types for backwards compatibility
export type { FindAndReplaceFunc } from "./dataTableTypes";

export function makeScheduleSettingsBuiltInDataTables(building: Building): BuiltInDataTable[] {
  const settingsOptions = building.settings.schedule?.scheduleSettingsTable;
  if (!settingsOptions) {
    return [];
  }

  const results: BuiltInDataTable[] = [];

  Object.entries(settingsOptions).forEach(([type, settingList]) => {
    const scheduleType = type as SystemDataSequenceEntityType;
    makeBuiltInDataTables(building, scheduleType, settingList, results);
  });

  return results;
}

export function makeBuiltInDataTables<T extends SystemDataSequenceEntityType>(
  building: Building,
  type: T,
  settingList: BuiltInDataTableSettings[],
  results: BuiltInDataTable[] = [],
  findAndReplace: FindAndReplaceFunc = defaultFindAndReplaceFunc,
  cellGenerator?: DataTableCellGenerator,
  headerGenerator?: DataTableHeaderGenerator,
  buildingEntityFilter?: BuildingEntityFilter,
  noSettings?: boolean,
  buildingOptionPricingContext?: DataTablePricingContext,
): BuiltInDataTable[] {
  if (settingList.length > 0) {
    if (type === "WINDOW") {
      for (const windowSetting of settingList as BuiltInDataTableSettings[]) {
        if (windowSetting.isActive) {
          results.push(selectWindows(building, windowSetting, findAndReplace, buildingEntityFilter));
        }
      }
    } else if (type === "DOOR") {
      for (const doorSetting of settingList as BuiltInDataTableSettings[]) {
        if (doorSetting.isActive) {
          results.push(selectDoors(building, doorSetting, findAndReplace, buildingEntityFilter, noSettings));
        }
      }
    } else {
      for (const entitySetting of settingList) {
        if (entitySetting.isActive) {
          results.push(
            makeBuiltInDataTable(
              building,
              entitySetting,
              findAndReplace,
              cellGenerator,
              headerGenerator,
              buildingEntityFilter,
              buildingOptionPricingContext,
            ),
          );
        }
      }
    }
  }

  return results;
}

function selectWindows(
  building: Building,
  dataTableSettings: BuiltInDataTableSettings,
  findAndReplace: FindAndReplaceFunc,
  buildingEntityFilter?: BuildingEntityFilter,
): BuiltInDataTable {
  const windowsList: BuiltInDataTableCell[][] = [];
  const countsByFloor = {};

  const windowDataSequence = makeBuiltInKAMDataSequence(
    building,
    dataTableSettings,
    BUILTIN_DATASEQUENCE_CATALOG["WINDOW"],
    findAndReplace,
    buildingEntityFilter,
  );

  for (const row of windowDataSequence.aggregatedKAMs) {
    const result = convertKAMToDataTableRow(row, dataTableSettings, findAndReplace);
    if (result) {
      windowsList.push(result);
    }
  }

  // Can't use aggregated KAMs for floors if it wasn't used as a defining attribute,
  // so use the un-aggregated KAMs instead
  for (const row of windowDataSequence.kams) {
    const levelKey = row.attributes.floor.value;
    countsByFloor[levelKey] = (countsByFloor[levelKey] || 0) + row.measures.count.value;
  }

  return {
    type: "WINDOW",
    name: dataTableSettings.scheduleName,
    headers: extractBuiltInDataTableSettingHeaderNames(dataTableSettings),
    rows: windowsList,
    countsByFloor,
    footnote: dataTableSettings.footnote,
  };
}

function selectDoors(
  building: Building,
  dataTableSettings: BuiltInDataTableSettings,
  findAndReplace: FindAndReplaceFunc,
  buildingEntityFilter?: BuildingEntityFilter,
  noSettings: boolean = false,
): BuiltInDataTable {
  const doorsList: BuiltInDataTableCell[][] = [];
  const countsByFloor = {};

  const withGarageDoors = noSettings || building.settings.planset.includeGarageDoorsInDoorSchedule;

  const selector: BuiltInKAMDataSequenceSelector<"DOOR"> = {
    ...BUILTIN_DATASEQUENCE_CATALOG["DOOR"],
    selector: (building, filter) => buildingDoorKAMs(building, filter, withGarageDoors),
  };

  const doorDataSequence = makeBuiltInKAMDataSequence(
    building,
    dataTableSettings,
    selector,
    findAndReplace,
    buildingEntityFilter,
  );

  for (const row of doorDataSequence.aggregatedKAMs) {
    const result = convertKAMToDataTableRow(row, dataTableSettings, findAndReplace);
    if (result) {
      doorsList.push(result);
    }
  }

  // Can't use aggregated KAMs for floors if it wasn't used as a defining attribute,
  // so use the un-aggregated KAMs instead
  for (const row of doorDataSequence.kams) {
    const levelKey = row.attributes.floor.value;
    countsByFloor[levelKey] = (countsByFloor[levelKey] || 0) + row.measures.count.value;
  }

  return {
    type: "DOOR",
    name: dataTableSettings.scheduleName,
    headers: extractBuiltInDataTableSettingHeaderNames(dataTableSettings),
    rows: doorsList,
    countsByFloor,
    footnote: dataTableSettings.footnote,
  };
}

function defaultFindAndReplaceFunc(value: string | number) {
  return value;
}

function buildingOptionKAMDataSequenceSelectorWithPricing(
  pricingContext: DataTablePricingContext,
): BuiltInKAMDataSequenceSelector<"BUILDING_OPTION"> {
  return {
    ...BUILTIN_DATASEQUENCE_CATALOG.BUILDING_OPTION,
    selector: (bldg, _filter) => buildingOptionKAMs(bldg, pricingContext).options,
  };
}

function makeBuiltInDataTable(
  building: Building,
  dataTableSettings: BuiltInDataTableSettings,
  findAndReplace: FindAndReplaceFunc,
  cellGenerator?: DataTableCellGenerator,
  headerGenerator?: DataTableHeaderGenerator,
  buildingEntityFilter?: BuildingEntityFilter,
  buildingOptionPricingContext?: DataTablePricingContext,
): BuiltInDataTable {
  const entitiesList: BuiltInDataTableCell[][] = [];

  const entitySelector: BuiltInDataSequenceSelector<typeof dataTableSettings.type> =
    dataTableSettings.type === "BUILDING_OPTION" && buildingOptionPricingContext
      ? buildingOptionKAMDataSequenceSelectorWithPricing(buildingOptionPricingContext)
      : BUILTIN_DATASEQUENCE_CATALOG[dataTableSettings.type];

  if (entitySelector.dataSequenceType === "TABLE_SEQUENCE") {
    return entitySelector.generateTable(building);
  }

  const entityDataSequence = makeBuiltInKAMDataSequence(
    building,
    dataTableSettings,
    entitySelector,
    findAndReplace,
    buildingEntityFilter,
  );

  for (const row of entityDataSequence.aggregatedKAMs) {
    const result = convertKAMToDataTableRow(row, dataTableSettings, findAndReplace, cellGenerator);
    if (result) {
      entitiesList.push(result);
    }
  }

  const headers = extractBuiltInDataTableSettingHeaderNames(dataTableSettings, headerGenerator);

  return {
    type: dataTableSettings.type,
    name: dataTableSettings.scheduleName,
    headers,
    rows: entitiesList,
    countsByFloor: {},
    footnote: dataTableSettings.footnote,
  };
}

export function extractBuiltInDataTableSettingHeaders(
  settings: BuiltInDataTableSettings,
  headerGenerator?: DataTableHeaderGenerator,
): Table<ColumnHeaderOptions> {
  const visibleHeaders: { [id: string]: ColumnHeaderOptions } = {};
  for (const attrib of Object.keys(settings.attributes)) {
    if (!settings.attributes[attrib].isHidden) {
      visibleHeaders[attrib] = settings.attributes[attrib];
    }
  }
  for (const measure of Object.keys(settings.measures)) {
    if (!settings.measures[measure].isHidden) {
      const key = encodeColumnConfigId(measure, { isAggregated: true });
      visibleHeaders[key] = settings.measures[measure];
    }
  }
  if (headerGenerator) {
    return headerGenerator(visibleHeaders);
  }
  return visibleHeaders;
}

function extractBuiltInDataTableSettingHeaderNames(
  settings: BuiltInDataTableSettings,
  headerGenerator?: DataTableHeaderGenerator,
): string[] {
  const headers = extractBuiltInDataTableSettingHeaders(settings, headerGenerator);
  return Object.entries(headers)
    .sort(([, a], [, b]) => (a.index ?? Infinity) - (b.index ?? Infinity))
    .map(([, { displayName }]) => (settings.forceColumnNameUppercase ? displayName.toUpperCase() : displayName));
}

const MISSING_ATTRIBUTE = keyAttributeNew("");

function missingAttribute(attributeName: string): KeyAttribute {
  console.warn(`missing attribute "${attributeName}"`);
  return MISSING_ATTRIBUTE;
}

function convertKAMToDataTableRow<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  kam: AggregatedKeyAttributesAndMeasures<Attributes, Measures>,
  settings: BuiltInDataTableSettings,
  findAndReplace: FindAndReplaceFunc,
  cellGenerator?: DataTableCellGenerator,
): BuiltInDataTableCell[] | undefined {
  const attributesWithOptions = Object.entries(settings.attributes).map(([attributeName, attributeOptions]) => ({
    name: attributeName,
    attributeOptions,
    attribute: kam.attributes[attributeName] ?? missingAttribute(attributeName),
  }));

  const measuresWithOptions = Object.entries(settings.measures)
    .filter(([, measure]) => !measure.isHidden)
    .map(([measureName, measureOptions]) => ({
      name: measureName,
      measureOptions,
      measure: kam.measures[measureName],
    }));

  // If an attribute would be filtered out, don't include the entire row
  for (const { name, attributeOptions, attribute } of attributesWithOptions) {
    if (!attributeFilterCheck(attributeOptions, findAndReplace(attribute.value, name, false) as string)) {
      return undefined;
    }
  }

  // Note: we filter out hidden attributes here, not above, because we want to allow the hidden
  // attributes to be used in the include/exclude filters via attributeFilterCheck
  const attributesByIndex: DataTableCellWithIndex[] = attributesWithOptions
    .filter(({ attributeOptions }) => !attributeOptions.isHidden)
    .map(({ name, attribute, attributeOptions }) => {
      const value = formatAttributeCellValue(attributeOptions, attribute.value as string);

      return { index: attributeOptions.index, cell: { type: "TEXT", value: findAndReplace(value, name, false) } };
    });

  const measuresByIndex: DataTableCellWithIndex[] = measuresWithOptions.map(({ name, measure, measureOptions }) => {
    const value = getNumericValueString(measureOptions, measure.value as number);
    return { index: measureOptions.index, cell: { type: "TEXT", value: findAndReplace(value, name, true) } };
  });

  const scheduleCellsWithIndex = [...attributesByIndex, ...measuresByIndex];
  const maxIndex = Math.max(...scheduleCellsWithIndex.map((kam) => kam.index));

  if (cellGenerator) {
    scheduleCellsWithIndex.push(...cellGenerator(kam.attributes, maxIndex));
  }

  return scheduleCellsWithIndex.sort((a, b) => a.index - b.index).map((kam) => kam.cell);
}

export function attributeFilterCheck(
  attributeOptions: Partial<Pick<KeyAttributeSettingOptions, "definesEntity" | "includeFilter" | "excludeFilter">>,
  attributeValue: string,
): boolean {
  if (!attributeOptions.definesEntity) {
    return true;
  }
  const includeFilter = attributeOptions.includeFilter?.trim();
  if (
    includeFilter &&
    includeFilter.length > 0 &&
    !includeFilter.split("|").some((filter) => attributeValue.includes(filter))
  ) {
    return false;
  }
  const excludeFilter = attributeOptions.excludeFilter?.trim();
  if (
    excludeFilter &&
    excludeFilter.length > 0 &&
    excludeFilter.split("|").some((filter) => attributeValue.includes(filter))
  ) {
    return false;
  }
  return true;
}

// Matches values starting with a zero followed by another digit (e.g. "0075", "007.5", "00"),
// but not "0" itself or decimals like "0.5"
const LEADING_ZERO_REGEX = /^0\d/;

/**
 * Formats a key attribute value for display in a data table cell. All-numeric values are
 * formatted per the attribute's unit options; values with leading zeros (e.g. option code
 * "0075") are identifiers rather than quantities, so they are returned verbatim.
 */
export function formatAttributeCellValue(attributeOptions: KeyAttributeSettingOptions, value: string): string {
  if (value === "" || LEADING_ZERO_REGEX.test(value) || !stringContainsOnlyNumericCharacters(value)) {
    return value;
  }
  return getNumericValueString(attributeOptions, Number(value));
}

function getNumericValueString(options: KeyAttributeSettingOptions | KeyMeasureSettingOptions, value: number): string {
  switch (options.unit) {
    case "INCHES":
      return formatInchesFraction(value);
    case "FEET":
      return `${Math.round(value * 100) / 100} FT`;
    case "SQ IN":
      return `${Math.round(value * 100) / 100} SQ IN`;
    case "SQ FT":
      return `${Math.round(value * 100) / 100} SF`;
    case "CU IN":
      return `${Math.round(value * 100) / 100} CU IN`;
    case "CU FT":
      return `${Math.round(value * 100) / 100} CF`;
    default:
      return `${Math.round(value * 100) / 100}`;
  }
}
